import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiry and unauthorized access - AUTO SIGN OUT
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const url = error.config?.url || "";

      // Never auto-logout on auth endpoint calls — those 401s are just wrong credentials
      const isAuthCall =
        url.includes("/auth/login") ||
        url.includes("/auth/mode") ||
        url.includes("/auth/register");

      if ((status === 401 || status === 403) && !isAuthCall) {
        const wasLoggedIn = !!localStorage.getItem("token");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        clearAuthModeCache();
        const onLoginPage =
          window.location.pathname === "/login" ||
          window.location.pathname === "/";
        if (!onLoginPage) {
          window.location.href = wasLoggedIn ? "/login?expired=1" : "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

// Auth API calls
export const authAPI = {
  login: async (identifier: string, password: string, twofa_token?: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      identifier,
      password,
      twofa_token,
    });
    return response.data;
  },

  register: async (userData: {
    id?: number;
    email?: string;
    password: string;
    full_name: string;
    role?: string;
  }) => {
    const response = await api.post("/auth/register", {
      ...userData,
      role: userData.role || "employee",
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get("/auth/me");
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.put("/auth/change-password", {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  },
};

// Helper functions
export const setAuthToken = (token: string) => {
  localStorage.setItem("token", token);
};

export const getAuthToken = () => {
  return localStorage.getItem("token");
};

export const setUser = (user: any) => {
  localStorage.setItem("user", JSON.stringify(user));
};

export const getUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

// Auth mode — fetched once at startup, cached in module scope
let _authMode: "password" | "authenticator" | null = null;

export const getAuthMode = async (): Promise<"password" | "authenticator"> => {
  if (_authMode) return _authMode;
  try {
    const r = await axios.get(`${API_BASE_URL}/auth/mode`);
    _authMode = r.data.mode || "password";
  } catch {
    _authMode = "password";
  }
  return _authMode!;
};

export const clearAuthModeCache = () => {
  _authMode = null;
};

export default api;
